import {AssistantIssueCreated, AssistantMessage, AssistantYesNoQuestion, UserMessage, UserPhoto} from "../api/issueReporting.ts";
import {FC, useMemo, useState} from "react";
import MessageBubble from "../atoms/MessageBubble.tsx";
import Markdown from "../atoms/Markdown.tsx";
import {PanelExtraVerticalPadding} from "../atoms/Panel.tsx";
import {Text} from "../style/typography.tsx";
import {Column, FlexSpace, Row} from "../atoms/Layout.tsx";
import SmallLoadingSpinner from "../atoms/SmallLoadingSpinner.tsx";
import {LocationIcon, SubtitleIcon, WarningIcon} from "../atoms/Icon";
import Button from "../atoms/Button.tsx";
import LargeLoadingSpinner from "../atoms/LargeLoadingSpinner.tsx";
import InputGroup from "./InputGroup.tsx";
import Logo from "../atoms/Logo.tsx";
import useTranslations from "../store/translations.ts";
import LinkButton from "../atoms/LinkButton.tsx";
import {addIssue} from "../api/issue.ts";

interface UserMessageProps {
    message: UserMessage
}

export const UserMessageDisplay: FC<UserMessageProps> = ({message}) => {
    return <MessageBubble participant="user">{message.content}</MessageBubble>
}

interface AssistantMessageProps {
    message: AssistantMessage
}

export const AssistantMessageDisplay: FC<AssistantMessageProps> = ({message}) => {
    return <MessageBubble participant="assistant"><Markdown markdown={message.content} /></MessageBubble>
}

interface AssistantYesNoQuestionProps {
    message: AssistantYesNoQuestion,
    onYesNoResponse: (response: 'yes' | 'no') => void,
    disabled: boolean
}

export const AssistantYesNoQuestionDisplay: FC<AssistantYesNoQuestionProps> = ({message, onYesNoResponse, disabled}) => {
    return <MessageBubble participant="assistant">
        <Column gap="16">
            <Markdown markdown={message.question} />
            <Row gap="12">
                <Button variant="outline" onClick={() => onYesNoResponse('yes')} disabled={disabled}>Yes</Button>
                <Button variant="outline" onClick={() => onYesNoResponse('no')} disabled={disabled}>No</Button>
            </Row>
        </Column>
    </MessageBubble>
}

interface UserPhotoProps {
    message: UserPhoto
}

export const UserPhotoMessageDisplay: FC<UserPhotoProps> = ({message}) => {
    return <MessageBubble participant="user"><img alt="" src={message.src} /></MessageBubble>
}

interface AssistantIssueCreatedProps {
    message: AssistantIssueCreated,
    onStartNewChat: () => void,
    photoFileKeys: Record<string, string>,
    siteId: string
}

// In format "Fri 30 May 17:23"
const currentTimeFormatted = (): string => {
    const now = new Date()
    const options: Intl.DateTimeFormatOptions = {weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'}
    return now.toLocaleString('en-US', options)
}

const IssueCreationForm: FC<{ initialValue: AssistantIssueCreated, onSubmit: (issue: AssistantIssueCreated) => void }> = ({initialValue, onSubmit}) => {
    const [title, setTitle] = useState(initialValue.title)
    const [description, setDescription] = useState(initialValue.description)
    const [location, setLocation] = useState(initialValue.location)
    const translations = useTranslations();

    const submit = () => {
        if (title !== "" && description !== "" && location !== "") {
            onSubmit({...initialValue, title, description, location})
        }
    }

    return <Column gap="20">
        <InputGroup label={translations.issueReportForm.titleFieldLabel} value={title} onChange={setTitle} error={title === "" ? translations.issueReportForm.titleErrorMissing : undefined} />
        <InputGroup label={translations.issueReportForm.descriptionFieldLabel} value={description} onChange={setDescription} error={description === "" ? translations.issueReportForm.descriptionErrorMissing : undefined} />
        <InputGroup label={translations.issueReportForm.locationFieldLabel} value={location} onChange={setLocation} error={location === "" ? translations.issueReportForm.locationErrorMissing : undefined} />
        <Row>
            <Button onClick={submit} disabled={title === "" || description === "" || location === ""}>{translations.issueReportForm.submitButton}</Button>
        </Row>
    </Column>
}

export const AssistantIssueCreatedMessageDisplay: FC<AssistantIssueCreatedProps> = ({ message, onStartNewChat, photoFileKeys, siteId }) => {
    const timeReported = useMemo(currentTimeFormatted, [])
    const [editing, setEditing] = useState(false)
    const [saving, setSaving] = useState(false)
    const [submitted, setSubmitted] = useState(false)
    const translations = useTranslations();

    const reportIssue = async (issue: AssistantIssueCreated) => {
        setSaving(true);
        const matchingPhotoFileKeys = issue.photoIds.map(photoId => photoFileKeys[photoId]);
        await addIssue(siteId, { title: issue.title, description: issue.description, location: issue.location, photoFileKeys: matchingPhotoFileKeys });
        setSaving(false);
        setSubmitted(true);
    }

    if (submitted) {
        return <PanelExtraVerticalPadding>
            <Column centerVertically centerHorizontally gap="20">
                <Logo size="large" />
                <Text role="heading" size="4" weight="semiBold">{translations.issueReportForm.successMessage}</Text>
                <Text role="body" size="1" weight="regular" center>
                    {translations.issueReportForm.successSubtitleMain}&nbsp;
                    <LinkButton onClick={onStartNewChat}>{translations.issueReportForm.successSubtitleLink}</LinkButton>
                </Text>
            </Column>
        </PanelExtraVerticalPadding>
    }

    if (saving) {
        return <PanelExtraVerticalPadding>
            <Column centerVertically centerHorizontally>
                <LargeLoadingSpinner />
            </Column>
        </PanelExtraVerticalPadding>
    }

    if (editing) {
        return <PanelExtraVerticalPadding>
            <IssueCreationForm initialValue={message} onSubmit={reportIssue} />
        </PanelExtraVerticalPadding>
    }

    return <PanelExtraVerticalPadding>
        <Column gap="28">
            <Column gap="12">
                <Row>
                    <Text role="heading" size="4" weight="semiBold">{translations.issueReportForm.issue}</Text>
                    <FlexSpace />
                    <Text role="body" size="2" weight="medium">{timeReported}</Text>
                </Row>
                <Row gap="12" centerVertically>
                    <LocationIcon size={20} />
                    <Text role="description" size="2" weight="medium">{message.location}</Text>
                </Row>
                <Row gap="12" centerVertically>
                    <WarningIcon size={20} />
                    <Text role="description" size="2" weight="medium">{message.title}</Text>
                </Row>
                <Row gap="12" centerVertically>
                    <SubtitleIcon size={20} />
                    <Text role="description" size="2" weight="medium">{message.description}</Text>
                </Row>
            </Column>
            <Row gap="20" centerVertically>
                <Column grow>
                    <Button variant="outline" onClick={() => setEditing(true)}>{translations.issueReportForm.editButton}</Button>
                </Column>
                <Column grow>
                    <Button variant="primary" onClick={() => reportIssue(message)}>{translations.issueReportForm.submitButton}</Button>
                </Column>
            </Row>
        </Column>
    </PanelExtraVerticalPadding>
}

export const WaitingForResponseMessageDisplay: FC = () => {
    return <MessageBubble participant="assistant">
        <SmallLoadingSpinner />
    </MessageBubble>
}